import { DatePicker, Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import {
  FormInstanceFieldItem,
  PciModuleDataUpdate,
  PciModuleInspectionOptions,
  PciType,
} from '@atom/types/form';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import { SchemaPavementConditionInspection } from '@atom/types/schema';

import classes from './pci.module.css';

const { MenuItem } = Select;

interface Props {
  fieldId: string;
  field: FormInstanceFieldItem;
  inventoryAsset: InventoryAssetDetailType;
  preview: boolean;
  schemas: SchemaPavementConditionInspection[];
  pciType: PciType;
  onPciTypeChange: (type: PciType, options: PciModuleInspectionOptions) => void;
  onChange: (update: PciModuleDataUpdate) => void;
  isSummary?: boolean;
  isEditable?: boolean;
}

const styles = {
  dateInputFloatingLabel: {
    color: colors.neutral.dim,
  },
  nameInput: {
    width: '10rem',
    marginRight: '2rem',
  },
  dateInput: {
    width: '10rem',
    marginRight: '2rem',
  },
  notesInput: {
    width: '35rem',
  },
  select: {
    width: '10rem',
  },
  menuItem: {
    color: colors.neutral.black,
    fontWeight: 500,
    fontSize: fonts.md,
  },
  menuItemSelected: {
    color: colors.brand.blue,
  },
};

const getPciTypes = (
  schemas: SchemaPavementConditionInspection[],
): string[] => {
  const types = new Set(
    schemas.reduce(
      (acc: string[], schema) => [...acc, ...Object.keys(schema.options)],
      [],
    ),
  );

  return [...types];
};

const getAttributeValue = (
  asset: InventoryAssetDetailType,
  attributeName: string,
): string => {
  if (!asset) {
    return '-';
  }

  const attribute = Object.values(asset.attributes || {}).find(
    ({ name }) => name === attributeName,
  );

  return attribute?.value || '-';
};

const PciFieldDetails = ({
  field,
  fieldId,
  inventoryAsset,
  preview,
  schemas,
  pciType,
  onPciTypeChange,
  onChange,
  isSummary,
  isEditable,
}: Props) => {
  const handlePciTypeChange = (type: any) => {
    const options = schemas.find(schema => schema.options[type])?.options[type];

    if (options) {
      onPciTypeChange(type, options);
    }
  };

  const handleChange =
    (property: keyof PciModuleDataUpdate) => (value: any) => {
      onChange({ fieldId, [property]: value });
    };

  return (
    <div className={classes.detailsContainer}>
      <div className={classes.subtitle}>Details</div>
      <div className={classes.detailContent}>
        <div className={classes.detail}>
          <div>Road</div>
          <div className={classes.detailValue}>
            {getAttributeValue(inventoryAsset, 'Road')}
          </div>
        </div>
        <div className={classes.detail}>
          <div>Location</div>
          <div className={classes.detailValue}>
            {getAttributeValue(inventoryAsset, 'PCI Location')}
          </div>
        </div>
        <div className={classes.detail}>
          <div>Sample No</div>
          <div className={classes.detailValue}>
            {getAttributeValue(inventoryAsset, 'Sample No')}
          </div>
        </div>
        <div className={classes.detail}>
          <div>Surface</div>
          {preview ? (
            <Select
              value={pciType}
              onChange={event => handlePciTypeChange(event.target.value)}
              style={styles.select}
              disabled={!preview}
            >
              {getPciTypes(schemas).map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <div className={classes.detailValue}>{pciType}</div>
          )}
        </div>
        <div className={classes.detail}>
          <div>Length</div>
          <div className={classes.detailValue}>
            {getAttributeValue(inventoryAsset, 'Sample Length (Feet)')}
          </div>
        </div>
        <div className={classes.detail}>
          <div>Width</div>
          <div className={classes.detailValue}>
            {getAttributeValue(inventoryAsset, 'Width (Feet)')}
          </div>
        </div>
        <div className={classes.detail}>
          <div>Sample Area</div>
          <div className={classes.detailValue}>
            {getAttributeValue(inventoryAsset, 'Sample Area (Sq Ft)')}
          </div>
        </div>
      </div>
      {!isSummary && (
        <div className={classes.inputsContainer}>
          <TextField
            label="Name"
            name="name"
            value={field?.pci?.name || ''}
            style={styles.nameInput}
            onChange={event => handleChange('name')(event.target.value)}
            disabled={!isEditable}
          />
          <div style={styles.dateInput}>
            <DatePicker
              value={field?.pci?.date ? new Date(field?.pci?.date) : null}
              onChange={updatedDate => {
                if (!updatedDate) {
                  handleChange('date')(null);
                } else {
                  handleChange('date')(updatedDate.valueOf());
                }
              }}
              name="date"
              disabled={!isEditable}
              label="Date"
              InputLabelProps={{ sx: styles.dateInputFloatingLabel }}
            />
          </div>
          <TextField
            multiline
            label="Notes"
            name="notes"
            style={styles.notesInput}
            value={field?.pci?.notes || ''}
            onChange={event => handleChange('notes')(event.target.value)}
            disabled={!isEditable}
          />
        </div>
      )}
    </div>
  );
};

export default PciFieldDetails;
